import type { Pixel } from "@pdfking/math"
import { AddMargins, Max, Min, ToSpatial } from "@pdfking/math"
import type { Executor, PlanFunction } from "@pdfking/planner"
import type { Page, PageNumber } from "@pdfking/renderer-api"
import { ConvertDistances } from "@pdfking/renderer-api"
import type { FileName } from "@pdfking/structs"
import { ArrayQ } from "@pdfking/structs"
import type { ExecutionContext } from "@pdfking/taskmaker"
import type { Partial, Sequence } from "@pdfking/tfunc"
import {
  Collect,
  Map as MapSync,
  MapAsync,
  MethodDispatcher,
  Preload,
  SortBy,
} from "@pdfking/tfunc"

import type { PdfEngine } from "#public"
import type { PdfEngineOptions } from "#public"
import type { MultiPagePdf } from "#public"
import type { RemoteFile } from "#public"
import type { SinglePageImage } from "#public"
import type { SinglePagePdf } from "#public"
import type { SinglePageTask } from "#public"
import type { ZipFile } from "#public"
import type { PageOrder } from "#public"
import type { PlottedPagePdf } from "#public"
import type { FileIdmlPlottingContext } from "#public"
import type { FilePlottingContext } from "#public"
import type { PlotSpec } from "#public"
import type { VirtualFile } from "#public"
import type { TasksType } from "#tasks"

import { CreatePdfEngineOptions } from "../../internal/engine/CreatePdfEngineOptions"
import { RasterNode } from "../../internal/files/RasterNode"
import { SplitNode } from "../../internal/files/SplitNode"
import { CreatePlottedFile } from "../files/CreatePlottedFile"
import { CreateRemoteFile } from "../files/CreateRemoteFile"
import { GenerateFile } from "../files/GenerateFile"
import { RequestFile } from "../files/RequestFile"
import { FileArray, FileLiteral } from "../methods/FileNodeGUID"
import { PAGE_ORDER_STRIDE } from "../types/files/mixin/PageOrder"
import { VirtualFileType } from "../types/files/VirtualFileType"

// A raster TaskPlot wrote at `dpi`, as the file of the page it is of
const PageImage = (source: SinglePagePdf, dpi: Pixel, task: SinglePageTask): SinglePageImage => ({
  ...task,
  dpi,
  enum: VirtualFileType.SINGLE_PAGE_IMAGE,
  file_meta: {},
  source,
})

// The rasterizer renders page_px * dpi / PDF_DPI pixels, page_px the page at
// pdf resolution
const PDF_DPI = 72 as Pixel

// `image_dpi`, lowered so the longest raster edge — the trim grown by the file
// bleed — stays within `max_size`, never raised
const EffectiveDpi = (options: PdfEngineOptions, page: SinglePagePdf): Pixel => {
  const { height, width } = ConvertDistances(
    options,
    AddMargins(ToSpatial(page.trim), options.file_bleed)
  )
  return Min(options.image_dpi, ((PDF_DPI * options.max_size) / Max(width, height)) as Pixel)
}

export const PdfEngineFromExecutor = (
  executor: Executor<TasksType, ExecutionContext>,
  config: Partial<PdfEngineOptions>
): PdfEngine => {
  const { plan, execute } = executor
  const options = CreatePdfEngineOptions(config)

  const file_literal = <T extends VirtualFile & Partial<PageOrder>>(file: T) =>
    FileLiteral(plan, file)
  const file_array = <T extends VirtualFile & Partial<PageOrder>>(files: T[]) =>
    FileArray(plan, files)

  const splitNode = (file: VirtualFile, batch: number) => SplitNode(executor, options, file, batch)

  // The pages a file rasterizes as, in order: an image is one already
  const pagesNode = MethodDispatcher<[VirtualFile], PlanFunction<SinglePagePdf | SinglePageImage>>(
    {
      [VirtualFileType.SINGLE_PAGE_IMAGE]: p => file_array([p]),
      [VirtualFileType.BINARY_PAGE_PDF]: p => file_array([p]),
      [VirtualFileType.PLOTTED_PAGE_PDF]: p => file_array([p]),
      [VirtualFileType.MULTI_PAGE_PDF]: p => file_array(p.pages),
    },
    p => splitNode(p, 0)
  )

  // TaskPlot writes the raster alone: the image is put back together here,
  // around the page it was asked for
  const rasterize = async (page: SinglePagePdf | SinglePageImage): Promise<SinglePageImage> => {
    if (page.enum === VirtualFileType.SINGLE_PAGE_IMAGE) return page
    const dpi = EffectiveDpi(options, page)
    return PageImage(page, dpi, await execute(RasterNode(executor, options, page, dpi)))
  }

  const SplitPdf = (files: Sequence<VirtualFile>) =>
    execute(plan.Preload(...MapSync(splitNode, files)))

  // files at once, each file's pages one after the other
  const RasterizePdf = (files: Sequence<VirtualFile>) =>
    Preload(...MapSync(file => MapAsync(rasterize, execute(pagesNode(file))), files))

  const mergeNode = (entry: VirtualFile | PlotSpec, batch: number): PlanFunction<SinglePagePdf> => {
    if (ArrayQ(entry)) {
      const [page, context] = entry
      return file_literal({
        ...CreatePlottedFile(options, page, context ?? {}),
        page_order: batch * PAGE_ORDER_STRIDE,
      })
    }
    return splitNode(entry as VirtualFile, batch)
  }

  const MergePdf = async (files: Sequence<VirtualFile | PlotSpec>): Promise<MultiPagePdf> => {
    // MapSync hands each document its request index as the batch, so every
    // collected page carries a globally unique page_order: one sort restores
    // the request order (the stream is collected here anyway), and pages are
    // relabeled dense so later merges of this file can trust array positions.
    // page_number/page_count are re-assigned too: each page's position now
    // refers to the merged document, not the source it was split from
    const collected = await Collect(execute(plan.Preload(...MapSync(mergeNode, files))))

    const page_count = collected.length as PageNumber
    const pages = SortBy(collected, page => page.page_order).map((page, page_order) => ({
      ...page,
      page_count,
      page_number: (page_order + 1) as PageNumber,
      page_order,
    }))

    return {
      enum: VirtualFileType.MULTI_PAGE_PDF,
      file_meta: {},
      pages,
    }
  }

  const ZipFiles = async (files: Sequence<[string, VirtualFile]>): Promise<ZipFile> => {
    const entries: Record<FileName, VirtualFile> = {}
    for (const [name, file] of files) {
      entries[name as FileName] = file
    }

    return {
      enum: VirtualFileType.ZIP_FILE,
      file_meta: {},
      files: entries,
    }
  }

  const PlotIdml = (pages: Page[], context: FileIdmlPlottingContext = {}): Promise<RemoteFile> =>
    execute(plan.TaskPlotIdml({ options, pages, context }))

  const PlotPdf = async (page: Page, context: FilePlottingContext = {}): Promise<PlottedPagePdf> =>
    CreatePlottedFile(options, page, context)

  return {
    CreateRemoteFile,
    config: options,
    GenerateFile: (stream, content) => GenerateFile(options, stream, content),
    MergePdf,
    PlotIdml,
    PlotPdf,
    RasterizePdf,
    RequestFile: (file, emit) => RequestFile(executor, options, file, emit),
    SplitPdf,
    ZipFiles,
  }
}
